import { useRef, useEffect } from 'react';

/**
 * Custom hook that ensures a function is called only once when a component mounts.
 * This prevents double API calls in React Strict Mode where components may mount twice.
 *
 * @param fn - The function to execute only once on component mount
 *
 * @example
 * ```tsx
 * useLoadOnce(() => {
 *   const loadData = async () => {
 *     const data = await fetchData();
 *     setData(data);
 *   };
 *   loadData();
 * });
 * ```
 */
export const useLoadOnce = (fn: () => void) => {
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (hasLoaded.current) return;
    hasLoaded.current = true;
    fn();
  }, [fn]);
};
